// src/components/TreatmentsOrdering.tsx

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    KeyboardSensor,
    MeasuringStrategy,
    PointerSensor,
    UniqueIdentifier,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { product_category } from '@prisma/client'; // Import category type
import { GripVertical, Loader2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import { Skeleton } from '~/components/ui/skeleton';

// --- Types ---

// Extend prisma types if needed, or use specific types for ordering
// Ensure Treatment type includes category details
type Treatment = {
    id: string;
    idx: number; // Assuming this is a display ID, not order index
    name: string;
    categoryId: number | null;
    category: product_category | null;
    // Add other relevant fields if needed for display
};

// Type for a category containing its treatments
type CategoryWithTreatments = {
    id: UniqueIdentifier; // Use UniqueIdentifier from dnd-kit, can be string or number
    name: string;
    treatments: Treatment[];
    // Optional: Add original category data if needed
    originalCategory: product_category | null;
};

// Type for the item being dragged (used in DragOverlay)
type ActiveItem = (CategoryWithTreatments & { type: 'category' }) | (Treatment & { type: 'treatment' });


// --- Utility Functions ---

const UNCATEGORIZED_ID = 'category-null';
const UNCATEGORIZED_NAME = 'Uncategorized';

// Helper to create unique dnd IDs
const getCategoryId = (catId: number | null): UniqueIdentifier => catId === null ? UNCATEGORIZED_ID : `category-${catId}`;
const getTreatmentId = (treatId: string): UniqueIdentifier => `treatment-${treatId}`;

// --- Draggable Item Components ---

interface TreatmentItemProps {
    treatment: Treatment;
    isDragging?: boolean; // Optional: for styling override during drag overlay
}

const TreatmentItem: React.FC<TreatmentItemProps> = ({ treatment, isDragging = false }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSortingDragging, // Differentiates item dragging from overlay dragging
    } = useSortable({
        id: getTreatmentId(treatment.id),
        data: { type: 'treatment', item: treatment }, // Pass data for onDragEnd
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isSortingDragging ? 0.5 : 1, // Style the original item when dragging
        cursor: isDragging ? 'grabbing' : 'grab',
        boxShadow: isDragging ? '0 2px 10px rgba(0,0,0,0.2)' : 'none',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex items-center p-2 bg-white border border-gray-200 rounded mb-2 ${isDragging ? 'z-10 relative' : ''}`} // Ensure overlay renders above others
        >
            <button
                {...attributes}
                {...listeners}
                className="p-1 mr-2 text-gray-500 hover:bg-gray-100 rounded cursor-grab focus:outline-none focus:ring-1 focus:ring-primary-400"
                aria-label={`Drag ${treatment.name}`}
            >
                <GripVertical className="h-4 w-4" />
            </button>
            <span className="text-sm truncate">{treatment.name}</span>
            {/* Add other treatment details if needed */}
        </div>
    );
};


interface CategoryItemProps {
    category: CategoryWithTreatments;
    isDragging?: boolean; // Optional: for styling override during drag overlay
}

const CategoryItem: React.FC<CategoryItemProps> = ({ category, isDragging = false }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSortingDragging, // Differentiates item dragging from overlay dragging
    } = useSortable({
        id: category.id,
        data: { type: 'category', item: category }, // Pass data for onDragEnd
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isSortingDragging ? 0.5 : 1, // Style the original item when dragging
        cursor: isDragging ? 'grabbing' : 'grab', // Style the overlay drag handle
    };

    // Get unique IDs for treatments within this category for the inner SortableContext
    const treatmentIds = useMemo(() => category.treatments.map(t => getTreatmentId(t.id)), [category.treatments]);

    return (
        <Card
            ref={setNodeRef}
            style={style}
            className={`mb-4 bg-gray-50 border border-gray-300 rounded-md ${isDragging ? 'z-10 relative shadow-lg' : ''} ${isSortingDragging ? 'opacity-50' : ''}`}
        >
            <CardHeader className="flex flex-row items-center justify-between p-3 border-b bg-gray-100 rounded-t-md">
                <CardTitle className="text-base font-medium">{category.name}</CardTitle>
                {/* Drag handle for the entire category */}
                <button
                    {...attributes}
                    {...listeners}
                    className="p-1 text-gray-600 hover:bg-gray-200 rounded cursor-grab focus:outline-none focus:ring-1 focus:ring-primary-400"
                    aria-label={`Drag category ${category.name}`}
                >
                    <GripVertical className="h-5 w-5" />
                </button>
            </CardHeader>
            <CardContent className="p-3">
                {/* Inner Sortable Context for Treatments */}
                <SortableContext items={treatmentIds} strategy={verticalListSortingStrategy}>
                    {category.treatments.length > 0 ? (
                        category.treatments.map(treatment => (
                            <TreatmentItem key={getTreatmentId(treatment.id)} treatment={treatment} />
                        ))
                    ) : (
                        <p className="text-sm text-gray-500 italic text-center py-2">No treatments in this category.</p>
                    )}
                </SortableContext>
            </CardContent>
        </Card>
    );
};


// --- Main Ordering Component ---

const TreatmentsOrdering = () => {
    const trpcUtils = trpc.useUtils();

    // --- State ---
    const [orderedCategories, setOrderedCategories] = useState<CategoryWithTreatments[]>([]);
    const [activeItem, setActiveItem] = useState<ActiveItem | null>(null); // For DragOverlay

    // --- Data Fetching ---
    const { data: treatmentsResponse, isLoading, error } = trpc.treatments.getAll.useQuery(
        {
            fetchAllForOrdering: true,
            filters: {}, // Add filters if necessary
        },
        {
            suspense: false, // Avoid suspense for easier loading state handling here
            // keepPreviousData: true, // May cause complexity with reordering state, be careful
        }
    );

    // --- Mutation Hook ---
    const updateOrderMutation = trpc.treatments.updateOrder.useMutation({
        onSuccess: () => {
            toast.success('Treatment order saved successfully!');
            // Optionally invalidate the query to refetch, though local state should be accurate
            trpcUtils.treatments.getAll.invalidate({ fetchAllForOrdering: true });
        },
        onError: (err) => {
            toast.error('Failed to save order', {
                description: err.message,
            });
            console.error("Update Order Error:", err);
        }
    });

    // --- Data Processing Effect ---
    // Group treatments by category and set initial order
    useEffect(() => {
        if (treatmentsResponse?.data) {
            const treatments = treatmentsResponse.data;
            const grouped = new Map<UniqueIdentifier, CategoryWithTreatments>();

            // Initialize Uncategorized group
            grouped.set(UNCATEGORIZED_ID, {
                id: UNCATEGORIZED_ID,
                name: UNCATEGORIZED_NAME,
                treatments: [],
                originalCategory: null,
            });

            // Group treatments
            treatments.forEach(treatment => {
                const catId = getCategoryId(treatment.categoryId);
                let categoryGroup = grouped.get(catId);

                // If category doesn't exist yet (other than Uncategorized)
                if (!categoryGroup && treatment.category) {
                    categoryGroup = {
                        id: catId,
                        name: treatment.category.name,
                        treatments: [],
                        originalCategory: treatment.category,
                    };
                    grouped.set(catId, categoryGroup);
                } else if (!categoryGroup && !treatment.category) {
                    // Should already be initialized, but as a fallback:
                    categoryGroup = grouped.get(UNCATEGORIZED_ID)!;
                }

                // Add treatment to the correct group
                categoryGroup!.treatments.push(treatment);
            });

            const sortedGrouped = Array.from(grouped.values());

            setOrderedCategories(sortedGrouped);
        }
    }, [treatmentsResponse?.data]);


    // --- Drag and Drop Setup ---
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Get all unique IDs for contexts
    const categoryIds = useMemo(() => orderedCategories.map(c => c.id), [orderedCategories]);
    const allTreatmentIds = useMemo(() => orderedCategories.flatMap(c => c.treatments.map(t => getTreatmentId(t.id))), [orderedCategories]);


    // --- Drag Handlers ---

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        const activeType = active.data.current?.type as 'category' | 'treatment';
        const activeItemData = active.data.current?.item;

        if (activeType && activeItemData) {
            setActiveItem({ ...activeItemData, type: activeType });
        } else {
            setActiveItem(null);
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveItem(null); // Clear overlay item

        if (!over || active.id === over.id) {
            return; // No change or dropped on itself
        }

        const activeType = active.data.current?.type as 'category' | 'treatment';
        const overType = over.data.current?.type as 'category' | 'treatment' | undefined; // Over might be a category container or a treatment

        setOrderedCategories((prevCategories) => {
            let newCategories = [...prevCategories]; // Shallow copy

            // --- Scenario 1: Dragging a Category ---
            if (activeType === 'category' && over) {
                const activeIndex = newCategories.findIndex(cat => cat.id === active.id);
                // Find the index of the category we dropped onto, or the category containing the item we dropped onto
                const overId = over.data.current?.type === 'category' ? over.id : over.data.current?.item?.categoryId ? getCategoryId(over.data.current?.item?.categoryId) : null;
                const overIndex = newCategories.findIndex(cat => cat.id === overId);


                if (activeIndex !== -1 && overIndex !== -1) {
                    newCategories = arrayMove(newCategories, activeIndex, overIndex);
                }
                return newCategories;
            }

            // --- Scenario 2: Dragging a Treatment ---
            if (activeType === 'treatment') {
                const activeTreatment = active.data.current?.item as Treatment;
                const sourceCategoryId = getCategoryId(activeTreatment.categoryId);

                // Find where the item was dropped
                let targetCategoryId: UniqueIdentifier | null = null;
                let targetIndex = -1; // Index within the target category's treatment list

                if (overType === 'treatment') {
                    // Dropped onto another treatment
                    const overTreatment = over.data.current?.item as Treatment;
                    targetCategoryId = getCategoryId(overTreatment.categoryId);
                    const targetCat = newCategories.find(cat => cat.id === targetCategoryId);
                    if (targetCat) {
                        targetIndex = targetCat.treatments.findIndex(t => getTreatmentId(t.id) === over.id);
                    }
                } else if (overType === 'category') {
                    // Dropped directly onto a category container (e.g., empty category)
                    targetCategoryId = over.id;
                    const targetCat = newCategories.find(cat => cat.id === targetCategoryId);
                    if (targetCat) {
                        targetIndex = targetCat.treatments.length; // Add to the end
                    }
                } else {
                    // Fallback: try to find category based on over.id if it matches a category ID format
                    const potentialCat = newCategories.find(cat => cat.id === over.id);
                    if (potentialCat) {
                        targetCategoryId = potentialCat.id;
                        targetIndex = potentialCat.treatments.length; // Add to end
                    }
                }


                if (targetCategoryId === null || targetIndex === -1) {
                    console.warn("Could not determine drop target category or index.");
                    return prevCategories; // No valid target found
                }

                const sourceCatIndex = newCategories.findIndex(cat => cat.id === sourceCategoryId);
                const targetCatIndex = newCategories.findIndex(cat => cat.id === targetCategoryId);

                if (sourceCatIndex === -1 || targetCatIndex === -1) {
                    console.warn("Could not find source or target category index.");
                    return prevCategories; // Should not happen if data is consistent
                }

                const sourceTreatments = [...newCategories[sourceCatIndex].treatments]; // Copy treatments
                const activeTreatmentIndex = sourceTreatments.findIndex(t => getTreatmentId(t.id) === active.id);

                if (activeTreatmentIndex === -1) {
                    console.warn("Could not find active treatment in source category.");
                    return prevCategories; // Should not happen
                }

                // Remove from source
                const [movedTreatment] = sourceTreatments.splice(activeTreatmentIndex, 1);

                // Update category ID if moved to a different category
                const newCategoryIdForTreatment = targetCategoryId === UNCATEGORIZED_ID ? null : newCategories[targetCatIndex].originalCategory?.id ?? null; // Get original Prisma ID or null
                movedTreatment.categoryId = newCategoryIdForTreatment;
                movedTreatment.category = newCategories[targetCatIndex].originalCategory; // Update relation


                if (sourceCategoryId === targetCategoryId) {
                    // --- Case 2a: Reordering within the same category ---
                    sourceTreatments.splice(targetIndex, 0, movedTreatment);
                    newCategories[sourceCatIndex] = { ...newCategories[sourceCatIndex], treatments: sourceTreatments };
                } else {
                    // --- Case 2b: Moving to a different category ---
                    const targetTreatments = [...newCategories[targetCatIndex].treatments]; // Copy treatments
                    targetTreatments.splice(targetIndex, 0, movedTreatment);

                    // Update both categories
                    newCategories[sourceCatIndex] = { ...newCategories[sourceCatIndex], treatments: sourceTreatments };
                    newCategories[targetCatIndex] = { ...newCategories[targetCatIndex], treatments: targetTreatments };
                }

                return newCategories;
            }

            // If logic didn't handle, return original state
            return prevCategories;
        });
    };

    // --- Render Logic ---

    if (isLoading) {
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <div className="flex justify-between items-center mb-6">
                        <h1 className='text-2xl font-bold'>Order Treatments</h1>
                        <Skeleton className="h-10 w-28" />
                    </div>
                    {/* Skeleton Loading for Categories */}
                    <div className="space-y-4">
                        {[1, 2, 3].map(i => (
                            <Card key={i}>
                                <CardHeader className="p-3 border-b bg-gray-100">
                                    <Skeleton className="h-6 w-1/2" />
                                </CardHeader>
                                <CardContent className="p-3 space-y-2">
                                    <Skeleton className="h-8 w-full" />
                                    <Skeleton className="h-8 w-full" />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </Content>
        );
    }

    if (error) {
        return (
            <Content>
                <div className='container mx-auto py-10'>
                    <h1 className='text-2xl font-bold mb-6'>Order Treatments</h1>
                    <p className="text-red-600">Error loading treatments: {error.message}</p>
                </div>
            </Content>
        );
    }

    return (
        <Content>
            <div className='container mx-auto py-10'>
                <div className="flex justify-between items-center mb-6">
                    <h1 className='text-2xl font-bold'>Order Treatments</h1>
                    <Button
                        onClick={() => {
                            // Prepare data for the mutation
                            const dataToSend = orderedCategories.map(category => ({
                                id: category.id.toString(), // Ensure ID is string ('category-...' or 'category-null')
                                treatmentIds: category.treatments.map(t => t.id) // Array of treatment UUIDs
                            }));

                            console.log("Sending Order Data:", dataToSend); // For debugging
                            updateOrderMutation.mutate({ orderedData: dataToSend });
                        }}
                        disabled={updateOrderMutation.isPending || isLoading} // Disable while saving or initial loading
                    >
                        {updateOrderMutation.isPending ? (
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        ) : null}
                        {updateOrderMutation.isPending ? 'Saving...' : 'Save Order'}
                    </Button>
                </div>

                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    measuring={{ droppable: { strategy: MeasuringStrategy.Always } }} // Helps with dynamic container sizes
                // Modifiers can prevent dragging outside bounds, etc.
                // modifiers={[restrictToVerticalAxis, restrictToWindowEdges]} // Apply if needed
                >
                    {/* Outer Sortable Context for Categories */}
                    <SortableContext items={categoryIds} strategy={verticalListSortingStrategy}>
                        {orderedCategories.map((category) => (
                            <CategoryItem key={category.id} category={category} />
                        ))}
                    </SortableContext>

                    {/* Drag Overlay for visual feedback */}
                    <DragOverlay dropAnimation={null} /* Optional: customize animation */ >
                        {activeItem ? (
                            activeItem.type === 'category' ? (
                                <CategoryItem category={activeItem as CategoryWithTreatments} isDragging={true} />
                            ) : (
                                <TreatmentItem treatment={activeItem as Treatment} isDragging={true} />
                            )
                        ) : null}
                    </DragOverlay>
                </DndContext>

                {orderedCategories.length === 0 && !isLoading && (
                    <p className="text-center text-gray-500 mt-8">No treatments found.</p>
                )}
            </div>
        </Content>
    );
};

export default TreatmentsOrdering;